import {navigate} from 'gatsby';
import add from 'date-fns/add';
import cookie from 'react-cookies';
import {LifetimePlanType} from '../payment.type';

export const redirectWithDelay = (redirectionUrl, delay = 3000) => {
  setTimeout(() => {
    if (redirectionUrl.startsWith('/')) {
      navigate(redirectionUrl);
    } else {
      window.location = redirectionUrl;
    }
  }, delay);
};

export const getParameterByName = (name, url) => {
  if (!url) url = window.location.href;
  name = name.replace(/[\[\]]/g, '\\$&');
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

export const getElementRefByClassName = (ref, classToAdd) => {
  return ref.current.querySelector(`.${classToAdd}`);
};

export const getElementsRefByClassName = (ref, classToAdd) => {
  return ref.current.querySelectorAll(`.${classToAdd}`);
};

// TODO: copy to new ts file
export const getFormattedPrice = (
    priceInSmallestUnitsOfCurrency, isoCurrencyCode, currencySymbol,
) => {
  let decimalisedNumber = priceInSmallestUnitsOfCurrency || 0;
  decimalisedNumber = decimalisedNumber.toFixed(2);
  if (isoCurrencyCode == 'gbp') return '£' + decimalisedNumber;
  else if (isoCurrencyCode == 'eur') return '€' + decimalisedNumber;
  else if (isoCurrencyCode == 'usd') return '$' + decimalisedNumber;
  else if (isoCurrencyCode == 'myr') return 'Rm' + decimalisedNumber;
  else if (isoCurrencyCode == 'inr') return '₹' + decimalisedNumber;
  else return currencySymbol + ' ' + decimalisedNumber;
};

export const PAGE_KINDS = {
  PREMIUM_TRIAL_SUBSCRIPTION: 'SubscriptionTrialCheckoutPage',
  PREMIUM_SUBSCRIPTION: 'SubscriptionCheckoutPage',
  COURSE_TOOLKIT: 'ToolkitCheckoutPage',
  COURSE_CERTIFICATE: 'ECertCheckoutPage',
  COURSE_HCD: 'HCDCheckoutPage',
  LIFETIME_BASIC: 'LifetimeBasicCheckoutPage',
  LIFETIME_FACULTY: 'LifetimeFacultyCheckoutPage',
  LIFETIME_UNLIMITED: 'LifetimeUnlimitedCheckoutPage',
  LIFETIME_STANDARD: 'LifetimeStandardCheckoutPage',
  THANKYOU_PAGE: 'SubscriptionThankYouPage',
  REDIRECTION_TO_THANKYOU: 'RedirectionToThankYouPage',
  UPDATE_PAYMENT: 'UpdatePaymentMethodPage',
  REFERRAL_CODE: 'RefferalCode',
  CARD_UPDATE: 'CardUpdate',
  VERIFY_PAGE: 'VerifyPage',
  TRIAL_INFORMATION: 'TrialInformationPage',
};

export const triggerPageViewAction = (studentId, pageKind, otherParams) => {
  window.dataLayer = window.dataLayer || [];
  const utmSource = getParameterByName('utm_source');
  const utmMedium = getParameterByName('utm_medium');
  const utmCampaign = getParameterByName('utm_campaign');
  setTimeout(() => {
    window.dataLayer.push({
      'event': 'view-page-action',
      'view-page-param': {
        'studentId': studentId,
        'pageKind': pageKind,
        'utmSource': utmSource,
        'utmMedium': utmMedium,
        'utmCampaign': utmCampaign,
        ...otherParams,
      },
    });
  }, 0);
  cookie.save('pageKind', pageKind);
};

export const triggerSubscriptionTrialAction = (
    studentId, pageKind, gateway, gatewayId,
    cardType, cardBrand, plan, coupon, predictedLTV,
    predictedLTV2, coursePicked, trialDays, totalPriceInEuro,
    chargeNowProductPrice, currency,
) => {
  setTimeout(() => {
    window.dataLayer.push({
      'event': 'subscription-trial-action',
      'subscription-event-param': {
        'identity': studentId,
        'pageKind': pageKind,
        'gateway': gateway,
        'gatewayId': gatewayId,
        'card_type': cardType,
        'card_brand': cardBrand,
        'product_code': plan,
        'coupon_code': coupon,
        'predicted_ltv': predictedLTV,
        'predicted_ltv2': predictedLTV2,
        'courseSlug': coursePicked,
        'trialDays': trialDays,
        'value': totalPriceInEuro,
        'productPrice': chargeNowProductPrice,
        'currency': currency?.toUpperCase(),
      },
    });
  }, 0);
  cookie.save('trialStarted', 1);
};

export const triggerSubscriptionAction = (
    studentId, pageKind, gateway, gatewayId,
    cardType, cardBrand, plan, coupon, predictedLTV,
    predictedLTV2, coursePicked, trialDays,
    recurringProductPrice, currency,
) => {
  setTimeout(() => {
    window.dataLayer.push({
      'event': 'subscription-action',
      'subscription-event-param': {
        'identity': studentId,
        'pageKind': pageKind,
        'gateway': gateway,
        'gatewayId': gatewayId,
        'card_type': cardType,
        'card_brand': cardBrand,
        'product_code': plan,
        'coupon_code': coupon,
        'predicted_ltv': predictedLTV,
        'predicted_ltv2': predictedLTV2,
        'courseSlug': coursePicked,
        'trialDays': trialDays,
        'productPrice': recurringProductPrice,
        'currency': currency?.toUpperCase(),
      },
    });
  }, 0);
};

export const triggerAddonsAction = (
    studentId, product, discount, productName, cardMeta,
) => {
  setTimeout(() => {
    window.dataLayer.push({
      'event': 'addon-purchase-action',
      'event-param': {
        'identity': studentId,
        'product': productName,
        'courseSlug': product.courseRegMeta?.courseSlug,
        'moduleNumber': product.courseRegMeta?.moduleNumber,
        'product_code': (
          product.courseRegMeta?.courseSlug + '_' + product.planId
        ),
        'coupon_code': discount?.coupon.couponCode,
        'addonId': product.planId,
        'quantity': 1,
        'card_type': cardMeta.cardType,
        'card_brand': cardMeta.cardBrand,
      },
    });
  }, 0);
};

export const getLifetimePageKind = (planType) => {
  if (planType === LifetimePlanType.BASIC) {
    return PAGE_KINDS.LIFETIME_BASIC;
  } else if (planType === LifetimePlanType.FACULTY) {
    return PAGE_KINDS.LIFETIME_FACULTY;
  } else if (planType === LifetimePlanType.STANDARD) {
    return PAGE_KINDS.LIFETIME_STANDARD;
  } else {
    return PAGE_KINDS.LIFETIME_UNLIMITED;
  }
};

export const triggerLifetimePurchaseAction = (
    studentId, pageKind, product, discount, productName, cardMeta,
) => {
  setTimeout(() => {
    window.dataLayer.push({
      'event': 'lifetime-purchase-action',
      'lifetime-purchase-param': {
        'identity': studentId,
        'product': productName,
        'pageKind': pageKind,
        'courseSlug': product.customAttributes?.lt_course_slug,
        'facultySlug': product.customAttributes?.lt_faculty_slug,
        'coupon_code': discount?.coupon.couponCode,
        'quantity': 1,
        'card_type': cardMeta.cardType,
        'card_brand': cardMeta.cardBrand,
      },
    });
  }, 0);
};

export const triggerCTAClickAction =
 (studentId, pageKind, ctaLabel, ctaAction) => {
   setTimeout(() => {
     window.dataLayer.push({
       'event': 'cta-click-action',
       'cta-click-param': {
         'studentId': studentId,
         'pageKind': pageKind,
         'cta': ctaLabel,
         'action': ctaAction,
       },
     });
   }, 0);
 };

export const triggerApiErrorAction = (pageKind, apiUrl, status) => {
  setTimeout(() => {
    window.dataLayer.push({
      'event': 'api-error-action',
      'api-error-param': {
        'pageKind': pageKind,
        'api': apiUrl,
        'status': status,
      },
    });
  }, 0);
};

export const triggerCardSubmissionFailure = (
    pageKind, locale, gateway, gatewayId, error,
) => {
  const errorMessage =
    error.getFirstErrorMessage ? error.getFirstErrorMessage() : error.message;
  const failureField =
    error.getFirstErrorField ? error.getFirstErrorField() : null;
  const errorCode = error.sourceError?.response?.data?.meta?.decline_code ||
  error.sourceError?.response?.code;
  setTimeout(() => {
    window.dataLayer.push({
      'event': 'card-submission-failed-event',
      'card-submission-failed-param': {
        'pageKind': pageKind,
        'locale': locale,
        'gateway': gateway,
        'gatewayId': gatewayId,
        'errorMessage': errorMessage,
        'errorCode': errorCode,
        'failureField': failureField,
      },
    });
  }, 0);
};

export const digestMessage = async (message) => {
  const msgUint8 =
    new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
  const hashBuffer =
    await window.crypto.subtle.digest('SHA-256', msgUint8); // hash the message
  const hashArray =
    Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
  const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join(''); // convert bytes to hex string
  return hashHex;
};
